import { Component, computed, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonDirective } from '@innersource/common-ui-components';
import { JobRunHistoryComponent } from '../utils/job-run-history/job-run-history.component';
import { CapabilityStore } from '../services/capability.store';
import { Tool, getDisplayName, isTool } from '../utils/models/tool.models';
import { Toolchain } from '../utils/models/toolchain.models';
import { routeId } from '../utils/route-param.util';
import { formatDateTime } from '../utils/helper';

type ResourceType = 'tool' | 'toolchain';

@Component({
  selector: 'app-capability',
  templateUrl: './capability.component.html',
  styleUrls: ['./capability.component.scss'],
  imports: [ButtonDirective, RouterLink, JobRunHistoryComponent],
})
export class CapabilityComponent {
  /** Services. */
  private readonly route = inject(ActivatedRoute);
  private readonly capabilityStore = inject(CapabilityStore);

  /** Template Forwards. */
  readonly isTool = isTool;
  readonly getDisplayName = getDisplayName;
  readonly formatDateTime = formatDateTime;

  /** State. */
  private readonly resourceType = this.getResourceType(this.route.snapshot.data['resourceType']);
  private readonly resourceId =
    routeId(
      this.route.snapshot.paramMap,
      this.resourceType === 'tool' ? 'toolId' : 'toolchainId'
    ) ?? 0;
  readonly isToolResource = this.resourceType === 'tool';
  readonly runHistory = this.isToolResource
    ? this.capabilityStore.jobHistory(this.resourceId)
    : this.capabilityStore.toolchainJobHistory(this.resourceId);
  readonly runLink = [this.isToolResource ? '/tools' : '/toolchains', this.resourceId, 'run'];

  /** Returns the selected tool or toolchain. */
  readonly resource = computed<Tool | Toolchain | undefined>(() =>
    this.resourceType === 'tool'
      ? this.capabilityStore.tool(this.resourceId)
      : this.capabilityStore.toolchain(this.resourceId)
  );

  /** Validates the route resource type. */
  private getResourceType(value: unknown): ResourceType {
    if (value === 'tool' || value === 'toolchain') return value;
    throw new Error('Capability route must specify a valid resource type.');
  }
}
